// 4.22-4.23 Send by hand: messages Ogma drafted for firms held for manual outreach only (2.37), which the owner sends
// from their own Instagram, Facebook or email account. Ogma never sends them. Pure helpers for the list
// (screens/approvals/SendByHand.tsx) and its tests.
import type { HandSend, HandSendChannel } from "./types";

/** Shown once at the top of the messages to send by hand (Craig, 16 Oct 2026; once a page since 18 Oct). */
export const HAND_SEND_WARNING =
  "Ogma won't send this for you. Check the rules on contacting sole traders in their country before you send it yourself.";

export const CHANNEL_LABEL: Record<HandSendChannel, string> = {
  instagram: "Instagram message",
  facebook: "Facebook message",
  email: "Email",
  call: "Phone call",
  linkedin: "LinkedIn",
};

export const HOLD_LABEL: Record<string, string> = {
  not_a_company: "Not a registered company",
  country_not_cleared: "Country not cleared for automatic sending",
  set_by_hand: "Held for manual outreach by you",
  email_unproven: "Address can't be fully checked (the domain accepts any address)",
};

const LANGUAGE_NAME: Record<string, string> = {
  en: "English", it: "Italian", fr: "French", de: "German", es: "Spanish", pt: "Portuguese", nl: "Dutch", el: "Greek",
  sv: "Swedish", da: "Danish", no: "Norwegian", fi: "Finnish", pl: "Polish", cs: "Czech", hr: "Croatian", sl: "Slovenian",
  ro: "Romanian", hu: "Hungarian", tr: "Turkish",
};

export function languageName(code: string | null | undefined): string | null {
  if (!code) return null;
  return LANGUAGE_NAME[code] ?? code.toUpperCase();
}

/** Only an https link to that site's own profile pages (the database builds them as https://www.<site>/<handle>). */
const SAFE_PROFILE: Record<"instagram" | "facebook", RegExp> = {
  instagram: /^https:\/\/(www\.)?instagram\.com\/[A-Za-z0-9._]{1,100}\/?$/,
  facebook: /^https:\/\/(www\.|m\.|web\.)?(facebook\.com|fb\.com)\/([A-Za-z0-9._-]{1,100}\/?|profile\.php\?id=\d{1,30})$/,
};

/** The mail service behind the business's connected mailbox, when known: its compose page is used for email by hand. */
export type MailService = "gmail" | "outlook" | null;

/** crm_mailbox_health's provider ("gmail", "microsoft"; the demo says "google") as the compose page to open. */
export function mailService(provider: string | null | undefined): MailService {
  const p = (provider ?? "").toLowerCase();
  if (p === "gmail" || p === "google") return "gmail";
  if (p === "microsoft" || p === "outlook" || p === "office365") return "outlook";
  return null;
}

/** A LinkedIn profile link (any country subdomain), https only. */
const SAFE_LINKEDIN = /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/(in|company|pub)\/[^\s?#<>"']{1,200}\/?$/i;

/** A LinkedIn people search for the person and their firm, when no profile link is known. */
export function linkedinSearch(name: string | null | undefined, firm: string | null | undefined): string | null {
  const words = [name, firm].map((w) => (w ?? "").trim()).filter(Boolean).join(" ");
  return words ? `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(words)}` : null;
}

/**
 * The one button for a message (4.22): it copies the message and opens the channel in one go. The Instagram or
 * Facebook profile (only those two sites), a compose window with the address, subject and body filled in (Gmail or
 * Outlook when the mailbox is known, otherwise mailto), or a tel link for a call. Null when there is nothing safe to
 * open; the card then offers Copy on its own.
 */
export function openLink(
  h: Pick<HandSend, "channel" | "profile_url" | "to_address" | "phone" | "subject" | "body"> & Partial<Pick<HandSend, "full_name" | "organisation_name">>,
  body = h.body,
  subject = h.subject ?? "",
  service: MailService = null,
): { href: string; label: string; external: boolean } | null {
  if (h.channel === "instagram" || h.channel === "facebook") {
    if (!h.profile_url || !SAFE_PROFILE[h.channel].test(h.profile_url)) return null;
    return { href: h.profile_url, label: h.channel === "instagram" ? "Copy and open Instagram" : "Copy and open Facebook", external: true };
  }
  if (h.channel === "linkedin") {
    if (h.profile_url && SAFE_LINKEDIN.test(h.profile_url)) return { href: h.profile_url, label: "Copy and open LinkedIn", external: true };
    const search = linkedinSearch(h.full_name, h.organisation_name);
    return search ? { href: search, label: "Copy and search LinkedIn", external: true } : null;
  }
  if (h.channel === "email") {
    if (!h.to_address || !/^[^\s@<>?&]+@[^\s@<>?&]+\.[^\s@<>?&]+$/.test(h.to_address)) return null;
    if (service) return { href: composeUrl(service, h.to_address, subject, body), label: service === "gmail" ? "Copy and open Gmail" : "Copy and open Outlook", external: true };
    return { href: mailto(h.to_address, subject, body), label: "Copy and open email", external: false };
  }
  const tel = (h.phone ?? "").replace(/[^\d+]/g, "");
  return tel ? { href: `tel:${tel}`, label: `Copy and call ${h.phone}`, external: false } : null;
}

/** A Gmail or Outlook web compose window with to, subject and body filled in, so nothing is retyped. */
export function composeUrl(service: "gmail" | "outlook", address: string, subject: string, body: string): string {
  const q = (pairs: [string, string][]) => pairs.filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v.replace(/\r\n/g, "\n"))}`).join("&");
  if (service === "gmail") return `https://mail.google.com/mail/?view=cm&fs=1&${q([["to", address], ["su", subject], ["body", body]])}`;
  return `https://outlook.office.com/mail/deeplink/compose?${q([["to", address], ["subject", subject], ["body", body]])}`;
}

/** mailto:<address>?subject=…&body=… with every part encoded (line breaks as %0D%0A, which mail apps expect). */
export function mailto(address: string, subject: string, body: string): string {
  const enc = (s: string) => encodeURIComponent(s).replace(/%0A/g, "%0D%0A").replace(/%0D%0D%0A/g, "%0D%0A");
  const parts = [subject ? `subject=${enc(subject)}` : "", body ? `body=${enc(body.replace(/\r\n/g, "\n"))}` : ""].filter(Boolean);
  return `mailto:${encodeURIComponent(address).replace(/%40/g, "@")}${parts.length ? `?${parts.join("&")}` : ""}`;
}

/** What to copy: an email's subject on its own line first, so it can be pasted into any mail app. */
export function copyText(h: Pick<HandSend, "channel">, body: string, subject: string): string {
  return h.channel === "email" && subject.trim() ? `${subject.trim()}\n\n${body}` : body;
}

/** The ways it could have gone instead (the firm's other contact points). */
export function otherChannels(h: Pick<HandSend, "channel" | "alternatives">): HandSendChannel[] {
  const map: Record<string, HandSendChannel> = { instagram: "instagram", facebook: "facebook", email: "email", phone: "call", linkedin: "linkedin" };
  return [...new Set((h.alternatives ?? []).map((a) => map[a]).filter((c): c is HandSendChannel => Boolean(c) && c !== h.channel))];
}

/** "Send by hand: 3 messages (2 Instagram, 1 email)". */
export function handSendSummary(list: Pick<HandSend, "channel">[]): string {
  if (!list.length) return "Nothing to send by hand";
  const counts = new Map<HandSendChannel, number>();
  for (const h of list) counts.set(h.channel, (counts.get(h.channel) ?? 0) + 1);
  const short: Record<HandSendChannel, string> = { instagram: "Instagram", facebook: "Facebook", email: "email", call: "call", linkedin: "LinkedIn" };
  const parts = [...counts.entries()].map(([c, n]) => `${n} ${short[c]}`);
  return `${list.length} ${list.length === 1 ? "message" : "messages"} to send yourself (${parts.join(", ")})`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Prospects › Drafts › Send yourself (job K-13, item 57; migration 20261226130000)
// ---------------------------------------------------------------------------------------------------------------------

/** All of a business's work outside a named campaign, and every message while campaigns are not set up. */
export const MAIN_CAMPAIGN = "Main campaign";

/** crm_hand_send_campaigns: the campaign (Find play) each waiting message belongs to. */
export interface HandSendCampaign {
  id: string;
  play_id: string | null;
  campaign_name: string | null;
}

export interface CampaignGroup<T> {
  key: string;
  name: string;
  items: T[];
}

/** The campaign of each waiting message; none (and no error) before the migration or when it can't be read. */
export async function handSendCampaigns(crm: { read<T>(fn: string, args?: Record<string, unknown>): Promise<T> }): Promise<HandSendCampaign[]> {
  try {
    const rows = await crm.read<HandSendCampaign[] | null>("crm_hand_send_campaigns");
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

/** The messages by campaign, in the order the first of each appears; a message with no campaign is in the main one. */
export function groupByCampaign<T extends { id: string; metadata?: unknown }>(items: T[], campaigns: HandSendCampaign[]): CampaignGroup<T>[] {
  const of = new Map(campaigns.map((c) => [c.id, c]));
  const groups = new Map<string, CampaignGroup<T>>();
  for (const h of items) {
    const c = of.get(h.id);
    const meta = (h.metadata ?? {}) as Record<string, unknown>;
    const play = c?.play_id ?? null;
    const key = play && c?.campaign_name ? play : "main";
    const name = key === "main" ? MAIN_CAMPAIGN : (c?.campaign_name ?? (typeof meta.campaign_name === "string" ? meta.campaign_name : MAIN_CAMPAIGN));
    const g = groups.get(key) ?? { key, name, items: [] };
    g.items.push(h);
    groups.set(key, g);
  }
  return [...groups.values()];
}

/** The one-line reason for this message: why now, else the hook, else why this channel. */
export function shortReason(h: Pick<HandSend, "metadata">): string | null {
  const meta = (h.metadata ?? {}) as Record<string, unknown>;
  const research = (meta.research ?? {}) as Record<string, unknown>;
  for (const v of [research.why_now, research.hook, meta.channel_reason]) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

/** Who it goes to, as the owner will type or find it: the address, the profile, or the number. */
export function contactLine(h: Pick<HandSend, "channel" | "to_address" | "profile_url" | "phone"> & Partial<Pick<HandSend, "full_name" | "organisation_name">>): string | null {
  if (h.channel === "email") return h.to_address ? `To ${h.to_address}` : null;
  if (h.channel === "call") return h.phone ? `Ring ${h.phone}` : null;
  if (h.channel === "linkedin") {
    if (h.profile_url && SAFE_LINKEDIN.test(h.profile_url)) return `LinkedIn: ${h.profile_url.replace(/^https:\/\/([a-z]{2,3}\.)?/i, "").replace(/\/$/, "")}`;
    const who = [h.full_name, h.organisation_name].filter(Boolean).join(", ");
    return who ? `LinkedIn: search for ${who}` : null;
  }
  if (!h.profile_url) return null;
  const m = /^https:\/\/(?:www\.|m\.|web\.)?[^/]+\/(.+?)\/?$/.exec(h.profile_url);
  const handle = m?.[1];
  if (!handle) return null;
  return `${h.channel === "instagram" ? "Instagram" : "Facebook"}: ${handle.startsWith("profile.php") ? h.profile_url : `@${handle}`}`;
}

/** The quiet line on how a sent message is noticed: email from her sent mail; the rest only when she says so. */
export function sentNote(h: Pick<HandSend, "channel">): string {
  if (h.channel === "email") return "If you forget to press Sent, Ogma ticks it off when it sees it in your sent mail.";
  if (h.channel === "call") return "Press Sent once you've made the call.";
  const site = h.channel === "instagram" ? "Instagram" : h.channel === "linkedin" ? "LinkedIn" : "Facebook";
  return `Press Sent once it's gone: Ogma can't see your ${site} messages yet.`;
}
